import {useState} from "react";
import {toast} from "sonner";
import {RotateCcw, Save} from "lucide-react";
import {Button} from "../../../basic/button";
import {Input} from "../../../basic/input";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {useUpdateLicenseQuotaUsageMutation, VwpQuota} from "../../../../store/generated/api";
import {formatDate} from "../../../../utils/dateUtils";

interface LicenseQuotasProps {
    licenseId: string;
    quotas: VwpQuota[];
}

export function LicenseQuotas({licenseId, quotas}: LicenseQuotasProps) {
    const {t} = useTranslationWithKeys();
    const {showErrorToast, handleApiError} = useErrorHandler();
    const [editedUsage, setEditedUsage] = useState<Record<string, string>>({});
    const [savingEntitlement, setSavingEntitlement] = useState<string | null>(null);

    const [updateLicenseQuotaUsage] = useUpdateLicenseQuotaUsageMutation();

    const getEntitlementLabel = (entitlement?: string) =>
        entitlement ? t(`licenses.quotas.${entitlement}`, entitlement) : t('licenses.quotas.unknownEntitlement');

    const getUsageValue = (quota: VwpQuota) => {
        const edited = quota.entitlement ? editedUsage[quota.entitlement] : undefined;
        return edited !== undefined ? edited : String(quota.currentUsage ?? 0);
    };

    const handleUsageChange = (entitlement: string, value: string) => {
        setEditedUsage(prev => ({...prev, [entitlement]: value}));
    };

    const formatPeriod = (quota: VwpQuota) => {
        if (!quota.periodStart && !quota.periodEnd) {
            return '-';
        }
        return [quota.periodStart, quota.periodEnd]
            .filter(Boolean)
            .map(date => formatDate(date))
            .join(' - ');
    };

    const handleSave = async (quota: VwpQuota, value: string) => {
        const entitlement = quota.entitlement;
        if (!entitlement) return;

        const currentUsage = Number(value);
        if (!Number.isInteger(currentUsage) || currentUsage < 0) {
            showErrorToast({message: t('admin.licenses.editLicense.quotas.invalidUsage')});
            return;
        }

        setSavingEntitlement(entitlement);
        try {
            await updateLicenseQuotaUsage({
                licenseId,
                entitlement,
                vwpUpdateQuotaUsageRequest: {currentUsage}
            }).unwrap();

            setEditedUsage(prev => {
                const next = {...prev};
                delete next[entitlement];
                return next;
            });
            toast.success(t('admin.licenses.editLicense.quotas.usageUpdated', {
                entitlement: getEntitlementLabel(entitlement)
            }));
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('admin.licenses.editLicense.quotas.updateFailed')});
        } finally {
            setSavingEntitlement(null);
        }
    };

    if (quotas.length === 0) {
        return (
            <div className="text-center py-8 text-muted-foreground">
                {t('admin.licenses.editLicense.quotas.noQuotas')}
            </div>
        );
    }

    return (
        <div className="border rounded-md">
            <table className="w-full">
                <thead>
                <tr className="border-b bg-muted/50">
                    <th className="px-4 py-2 text-left text-sm font-medium">
                        {t('admin.licenses.editLicense.quotas.entitlement')}
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium">
                        {t('admin.licenses.editLicense.quotas.currentUsage')}
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium">
                        {t('admin.licenses.editLicense.quotas.softLimit')}
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium">
                        {t('admin.licenses.editLicense.quotas.hardLimit')}
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium">
                        {t('admin.licenses.editLicense.quotas.period')}
                    </th>
                    <th className="px-4 py-2 text-right text-sm font-medium">
                        {t('admin.licenses.editLicense.quotas.actions')}
                    </th>
                </tr>
                </thead>
                <tbody>
                {quotas.map((quota, index) => {
                    const value = getUsageValue(quota);
                    const isDirty = value !== String(quota.currentUsage ?? 0);
                    const isSaving = savingEntitlement === quota.entitlement;

                    return (
                        <tr key={quota.entitlement || index} className="border-b last:border-b-0">
                            <td className="px-4 py-2 text-sm">
                                {getEntitlementLabel(quota.entitlement)}
                            </td>
                            <td className="px-4 py-2">
                                <Input
                                    type="number"
                                    min={0}
                                    value={value}
                                    disabled={!quota.entitlement || isSaving}
                                    onChange={(e) => quota.entitlement && handleUsageChange(quota.entitlement, e.target.value)}
                                    className="h-8 w-28"
                                />
                            </td>
                            <td className="px-4 py-2 text-sm text-muted-foreground">
                                {quota.limit ?? t('admin.licenses.editLicense.quotas.unlimited')}
                            </td>
                            <td className="px-4 py-2 text-sm text-muted-foreground">
                                {quota.hardLimit ?? t('admin.licenses.editLicense.quotas.unlimited')}
                            </td>
                            <td className="px-4 py-2 text-sm text-muted-foreground">
                                {formatPeriod(quota)}
                            </td>
                            <td className="px-4 py-2">
                                <div className="flex items-center justify-end gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={!quota.entitlement || isSaving || (quota.currentUsage ?? 0) === 0}
                                        onClick={() => handleSave(quota, "0")}
                                    >
                                        <RotateCcw className="h-4 w-4 mr-1"/>
                                        {t('admin.licenses.editLicense.quotas.reset')}
                                    </Button>
                                    <Button
                                        size="sm"
                                        disabled={!isDirty || isSaving}
                                        onClick={() => handleSave(quota, value)}
                                    >
                                        <Save className="h-4 w-4 mr-1"/>
                                        {isSaving ? t('common.saving') : t('common.save')}
                                    </Button>
                                </div>
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
}
